/**
 * Workspace Slice
 * 
 * Redux slice for managing workspace state including:
 * - Current active workspace
 * - List of user's workspaces
 * - Workspace switching logic
 */

import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { workspacesAPI } from '../../services/api';

export interface Workspace {
  id: string;
  name: string;
  description?: string;
  type: 'PERSONAL' | 'ORGANIZATION';
  role: string;
  createdAt: string;
  _count?: {
    items: number;
    categories: number;
    members?: number;
  };
}

interface WorkspaceState {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

const initialState: WorkspaceState = {
  workspaces: [],
  currentWorkspace: null,
  loading: false,
  error: null,
  initialized: false,
};

// Async thunks
export const fetchWorkspaces = createAsyncThunk(
  'workspace/fetchWorkspaces',
  async (_, { rejectWithValue }) => {
    try {
      const response = await workspacesAPI.getAll();
      return response.data?.workspaces || response.data || [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch workspaces');
    }
  }
);

export const switchWorkspace = createAsyncThunk(
  'workspace/switchWorkspace',
  async (workspaceId: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { workspace: WorkspaceState };
      const workspace = state.workspace.workspaces.find(w => w.id === workspaceId);
      
      if (!workspace) {
        throw new Error('Workspace not found');
      }
      
      // Store the preference
      localStorage.setItem('currentWorkspaceId', workspaceId);
      
      return workspace;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to switch workspace');
    }
  }
);

export const createWorkspace = createAsyncThunk(
  'workspace/createWorkspace',
  async (data: { name: string; description?: string; type: 'PERSONAL' | 'ORGANIZATION' }, { dispatch, rejectWithValue }) => {
    try {
      const response = await workspacesAPI.create(data);
      // Refetch all workspaces to get updated list
      dispatch(fetchWorkspaces());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create workspace');
    }
  }
);

const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    setCurrentWorkspace: (state, action: PayloadAction<Workspace | null>) => {
      state.currentWorkspace = action.payload;
      if (action.payload) {
        localStorage.setItem('currentWorkspaceId', action.payload.id);
      } else {
        localStorage.removeItem('currentWorkspaceId');
      }
    },
    clearWorkspaces: (state) => {
      state.workspaces = [];
      state.currentWorkspace = null;
      state.initialized = false;
      localStorage.removeItem('currentWorkspaceId');
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch workspaces
    builder.addCase(fetchWorkspaces.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchWorkspaces.fulfilled, (state, action) => {
      state.loading = false;
      state.workspaces = action.payload;
      state.initialized = true;
      
      // Restore current workspace from localStorage or set first one
      const storedId = localStorage.getItem('currentWorkspaceId');
      if (storedId) {
        const stored = action.payload.find((w: Workspace) => w.id === storedId);
        if (stored) {
          state.currentWorkspace = stored;
        } else if (action.payload.length > 0) {
          state.currentWorkspace = action.payload[0];
          localStorage.setItem('currentWorkspaceId', action.payload[0].id);
        }
      } else if (action.payload.length > 0) {
        state.currentWorkspace = action.payload[0];
        localStorage.setItem('currentWorkspaceId', action.payload[0].id);
      }
    });
    builder.addCase(fetchWorkspaces.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      state.initialized = true;
    });
    
    // Switch workspace
    builder.addCase(switchWorkspace.fulfilled, (state, action) => {
      state.currentWorkspace = action.payload;
    });
    builder.addCase(switchWorkspace.rejected, (state, action) => {
      state.error = action.payload as string;
    });
    
    // Create workspace
    builder.addCase(createWorkspace.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createWorkspace.fulfilled, (state, action) => {
      state.loading = false;
      // New workspace will be added when fetchWorkspaces completes
      // Optionally set as current
      if (action.payload) {
        state.currentWorkspace = action.payload;
        localStorage.setItem('currentWorkspaceId', action.payload.id);
      }
    });
    builder.addCase(createWorkspace.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { setCurrentWorkspace, clearWorkspaces, clearError } = workspaceSlice.actions;
export default workspaceSlice.reducer;
